import React, { useCallback } from 'react';
import {
  createStyles,
  Theme,
  makeStyles,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
} from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import { UserAvatar } from 'src/components/User';
import { itemHeight, TruncatedText } from 'src/components/UI';
import * as Weights from 'src/theme/weights';
import { NotificationBadge } from 'src/components/UI/Badges';
import { useChannelPreviewData } from 'src/components/Channels/useChannelPreviewData';
import { ChannelType } from 'src/components/Channels/useChannelForm';
import { FormsNotificationsStatuses } from 'src/store/notifications/types';
import { ExtensionItemStatus } from 'src/store/dashboard/types';
import { FileChannel } from 'src/store/files/types';
import {
  typography13MediumStyle,
  typography13RegularStyle,
} from 'src/components/Text';
import { BlackHeadings, GraySmall, NonHoverBackground } from 'src/theme/colors';

export interface AvatarInfoType {
  name: string;
  imageUrl: string;
  fallbackColor?: string;
}

export interface ChannelOptionData {
  companyID?: string;
  fileChannel?: FileChannel;
  extensionMemberIds?: string[];
  id?: string;
  clientId?: string;
  name: string;
  avatarInfo: AvatarInfoType[];
  initialLetters?: string;
  position?: number;
  status?: FormsNotificationsStatuses | ExtensionItemStatus;
  type?: string;
}

export interface ChannelSidebarItemProps {
  channelType: ChannelType;
  isSelected: boolean;
  style: React.CSSProperties;
  channelOption: ChannelOptionData;
  onSelect: (channelData: ChannelOptionData) => void;
  isRootSelected?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentItem: {
      height: `${itemHeight}px`,
      borderRadius: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1, 0, 1),
      color: theme.palette.primary.main,
      transition: '0.15s',
      cursor: 'pointer',
      [theme.breakpoints.down('xs')]: {
        maxWidth: 'initial',
        padding: 0,
      },
      [theme.breakpoints.up('sm')]: {
        '&:hover': {
          backgroundColor: NonHoverBackground,
        },
      },
    },
    selectedItem: {
      '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: NonHoverBackground,
      },
    },
    time: {
      textAlign: 'right',
      color: '#000',
      fontSize: '12px',
      fontWeight: Weights.medium,
    },
    notificationCount: {
      float: 'right',
    },
  }),
);

export const ChannelSidebarItem: React.FC<ChannelSidebarItemProps> = React.memo(
  ({
    channelOption,
    onSelect,
    isSelected,
    style,
    channelType,
    isRootSelected,
  }) => {
    const { avatarInfo, name } = channelOption;

    const classes = useStyles();
    const theme = useTheme();

    const handleChannelClicked = () => {
      // only select channel if its not already selected or
      // the ROOT channel is not selected
      if (!isSelected || !isRootSelected) {
        onSelect(channelOption);
      }
    };

    const {
      getChannelSummary,
      getTotalUnreadNotificationsCount,
    } = useChannelPreviewData(channelOption, channelType);

    const {
      time: channelSummaryTime,
      subTitle: channelSummarySubtitle,
    } = getChannelSummary();
    const channelNotificationCounter = getTotalUnreadNotificationsCount();

    const avatarInitials = useCallback(
      (nameValue: string) => {
        const { fileChannel, companyID } = channelOption;

        if (companyID || fileChannel?.fields.companyID)
          return nameValue.split('')[0];

        return nameValue;
      },
      [channelOption],
    );

    return (
      <Box
        style={{
          ...style,
          padding: theme.spacing(0, 0, 1, 0),
        }}
      >
        <ListItem
          className={classes.contentItem}
          classes={{ selected: classes.selectedItem }}
          selected={isSelected}
          onClick={handleChannelClicked}
          key={`${channelType}-channel-${name}`}
        >
          <Box
            mr={2}
            position="relative"
            minWidth={40}
            maxWidth={40}
            height={40}
          >
            {avatarInfo
              .slice(0, 2)
              .map(({ name: avatarName, imageUrl, fallbackColor }, index) => (
                <Box
                  position={avatarInfo.length > 1 ? 'absolute' : 'unset'}
                  left={15 * (1 - index)}
                  bottom={15 * (1 - index)}
                  zIndex={1 - index}
                  key={avatarName}
                >
                  <UserAvatar
                    type="image"
                    initialLetters={avatarInitials(avatarName)}
                    name={avatarName}
                    showName={false}
                    avatarUrl={imageUrl}
                    fallbackColor={fallbackColor}
                    avatarSize={avatarInfo.length > 1 ? 'xxsmall' : 'small'}
                  />
                </Box>
              ))}
          </Box>

          <ListItemText
            primary={
              <TruncatedText
                styleProps={{
                  ...typography13MediumStyle,
                  fontWeight: isSelected ? Weights.semiBold : Weights.medium,
                  color: BlackHeadings,
                }}
                text={name}
              />
            }
            secondary={
              <TruncatedText
                text={channelSummarySubtitle}
                styleProps={{
                  '&::firstLetter': {
                    textTransform: 'capitalize',
                  },
                  color: GraySmall,
                  ...typography13RegularStyle,
                }}
              />
            }
          />
          {/* Should replace primary value and notification badge content with real variable in the following component */}
          <ListItemIcon>
            <ListItemText
              classes={{
                primary: classes.time,
                secondary: classes.notificationCount,
              }}
              primary={channelSummaryTime}
              secondary={
                <NotificationBadge
                  showZero={false}
                  content={channelNotificationCounter}
                />
              }
            />
          </ListItemIcon>
        </ListItem>
      </Box>
    );
  },
);
